import React, { useState } from 'react';
// Deployment trigger - updated 2025-01-27 v3
import { Link } from 'react-router-dom';
import ProcessFlowHero from '../components/ProcessFlowHero';
import { Accordion } from '../components/Accordion';
import { 
  Database, 
  Target, 
  ArrowRight,
  Zap,
  Settings,
  TrendingUp,
  Layers,
  FileEdit,
  ChevronRight,
  Map,
  Mountain,
  Rocket,
  Users,
  BarChart3,
  StickyNote,
  BookOpen,
  FlaskConical,
  Sparkles,
  Compass,
  CheckCircle
} from 'lucide-react';
import { mofLayers, metaStackAsM3 } from '../data/mofArchitecture';
import { masterRegistryEntries } from '../data/masterRegistryEntries';
import { designTimeTemplates } from '../data/designTimeExamples';
import { runTimeInstances } from '../data/runTimeExamples';
import { M2AsM1Template } from '../components/M2AsM1Template';

export const AboutArchived: React.FC = () => {
  const [selectedLayer, setSelectedLayer] = useState<string | null>(null);

  const getIcon = (iconName: string) => {
    const icons: Record<string, any> = {
      Settings,
      Layers,
      Database,
      FileEdit,
      Zap
    };
    return icons[iconName] || Layers;
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, any> = {
      purple: {
        bg: 'bg-[var(--mustard-dim)]',
        border: 'border-[var(--mustard)]',
        text: 'text-[var(--mustard)]',
        accent: 'text-[var(--mustard)]',
        badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
        button: 'bg-[var(--mustard)] hover:bg-[var(--mustard)] text-white'
      },
      indigo: {
        bg: 'bg-sage-50',
        border: 'border-sage-300',
        text: 'text-sage-900',
        accent: 'text-sage-600',
        badge: 'bg-sage-100 text-sage-800',
        button: 'bg-sage-600 hover:bg-sage-700 text-white'
      },
      blue: {
        bg: 'bg-consulting-slate-blue-50',
        border: 'border-consulting-slate-blue-300',
        text: 'text-consulting-slate-blue-900',
        accent: 'text-consulting-slate-blue-600',
        badge: 'bg-consulting-slate-blue-100 text-consulting-slate-blue-800',
        button: 'bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700 text-white'
      },
      green: {
        bg: 'bg-sage-50',
        border: 'border-sage-300',
        text: 'text-sage-900',
        accent: 'text-sage-600',
        badge: 'bg-sage-100 text-sage-800',
        button: 'bg-sage-600 hover:bg-sage-700 text-white'
      },
      orange: {
        bg: 'bg-[var(--mustard-dim)]',
        border: 'border-[var(--mustard)]',
        text: 'text-[var(--mustard)]',
        accent: 'text-[var(--mustard)]',
        badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
        button: 'bg-[var(--mustard)] hover:bg-[var(--mustard)] text-white'
      },
      coral: {
        bg: 'bg-coral-50',
        border: 'border-coral-300',
        text: 'text-coral-900',
        accent: 'text-coral-600',
        badge: 'bg-coral-100 text-coral-800',
        button: 'bg-coral-600 hover:bg-coral-700 text-white'
      }
    };
    return colors[color] || colors.blue;
  };

  const getLayerData = (layerId: string) => {
    switch (layerId) {
      case 'm3':
        return {
          title: 'Meta-Stack Layers',
          count: 4,
          items: metaStackAsM3.layers.map(l => `${l.name}: ${l.description} (${l.provides})`)
        };
      case 'm2':
        return {
          title: 'Registry Entries',
          count: masterRegistryEntries.length,
          items: masterRegistryEntries.slice(0, 10).map(e => `${e.canonicalName} (${e.shortCode})`)
        };
      case 'm1':
        return {
          title: 'Templates',
          count: designTimeTemplates.length,
          items: designTimeTemplates.map(t => `${t.name} [based on ${t.basedOnName}]`)
        };
      case 'm0':
        return {
          title: 'Runtime Instances',
          count: runTimeInstances.length,
          items: runTimeInstances.map(i => `${i.name} - ${i.participants} participant(s), ${i.status}`)
        };
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4 pt-6 pb-8">
        <div className="flex items-center justify-center space-x-3 mb-2">
          <Mountain className="h-16 w-16 text-consulting-slate-blue-600" />
          <h1 className="text-6xl font-display font-normal text-charcoal-900">
            Elevation
          </h1>
        </div>
        <p id="site-header-tagline" className="text-xl text-charcoal-600 font-body">
          Clarity Achieved. Align on what matters most. Move forward with confidence.
        </p>
      </div>

      {/* Process Flow Documentation */}
      <div className="mb-12">
        <ProcessFlowHero />
      </div>

      {/* Four Layer Knowledge Architecture - MOVED TO TOP */}
      <div className="mb-12">
        <h2 className="text-2xl font-heading font-bold text-charcoal-900 mb-6 text-center">The Four-Layer Knowledge Architecture</h2>
        <p className="text-lg text-charcoal-700 font-body text-center mb-8 max-w-3xl mx-auto">
          M3-M0: From metadata to runtime instances
        </p>
        
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="p-6 border-l-4 border-consulting-slate-blue-600">
            <h4 className="text-xl font-heading font-bold text-charcoal-900 mb-4">M3: Metadata</h4>
            <p className="text-charcoal-700 font-body mb-4">
              The universal grammar for representing all knowledge. Defines the fields and structure for all models.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-consulting-slate-blue-600 rounded-full"></div>
                <span className="text-sm text-charcoal-600">Ontology</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-sage-600 rounded-full"></div>
                <span className="text-sm text-charcoal-600">Modality</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[var(--mustard)] rounded-full"></div>
                <span className="text-sm text-charcoal-600">Dynamics</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-coral-600 rounded-full"></div>
                <span className="text-sm text-charcoal-600">Evaluation</span>
              </div>
            </div>
          </div>
          
          <div className="p-6 border-l-4 border-sage-600">
            <h4 className="text-xl font-heading font-bold text-charcoal-900 mb-4">M2: Models</h4>
            <p className="text-charcoal-700 font-body mb-4">
              Concrete frameworks like DEGAP, Johari Window, Logical Levels. Define structure, relationships, and rules.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-consulting-slate-blue-600 rounded-full"></div>
                <span className="text-sm text-charcoal-600">Decision Models</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-sage-600 rounded-full"></div>
                <span className="text-sm text-charcoal-600">Assessment Models</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[var(--mustard)] rounded-full"></div>
                <span className="text-sm text-charcoal-600">Process Models</span>
              </div>
            </div>
          </div>
          
          <div className="p-6 border-l-4 border-coral-600">
            <h4 className="text-xl font-heading font-bold text-charcoal-900 mb-4">M1: Templates</h4>
            <p className="text-charcoal-700 font-body mb-4">
              Ready-to-deploy templates based on M2 models. Convert models into usable forms with specific fields and validation.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-coral-600 rounded-full"></div>
                <span className="text-sm text-charcoal-600">Assessment Templates</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-coral-600 rounded-full"></div>
                <span className="text-sm text-charcoal-600">Workshop Templates</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-coral-600 rounded-full"></div>
                <span className="text-sm text-charcoal-600">Planning Templates</span>
              </div>
            </div>
          </div>
          
          <div className="p-6 border-l-4 border-[var(--mustard)]">
            <h4 className="text-xl font-heading font-bold text-charcoal-900 mb-4">M0: Runtime</h4>
            <p className="text-charcoal-700 font-body mb-4">
              Actual experiences and instances. Real workshops, assessments, and interventions with measurable outcomes.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[var(--mustard)] rounded-full"></div>
                <span className="text-sm text-charcoal-600">Live Workshops</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[var(--mustard)] rounded-full"></div>
                <span className="text-sm text-charcoal-600">Client Sessions</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[var(--mustard)] rounded-full"></div>
                <span className="text-sm text-charcoal-600">Measured Outcomes</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Opening Statement */}
      <div className="text-center py-6">
        <p className="text-2xl text-charcoal-700 font-body leading-relaxed max-w-4xl mx-auto">
          Peak Priorities is a clarity company. We help people and teams align on what matters most and move forward with confidence—by turning complexity into coherent action.
        </p>
      </div>

      {/* Section Separator */}
      <div className="border-t border-charcoal-200 my-12"></div>

      {/* Main Content Accordion */}
      <div className="mb-8">
        <Accordion 
          items={[
            {
              id: 'mission',
              title: 'Our Mission & Vision',
              content: (
                <div className="space-y-4">
                  <p className="text-consulting-slate-blue-800 font-heading font-medium text-xl">
                    Clarity Achieved. Align on what matters most. Move forward with confidence.
                  </p>
                  <p className="text-consulting-slate-blue-700 text-lg leading-relaxed font-body">
                    Our purpose is to ignite a real sense of possibility, energize leaders, and embolden 
                    their teams by helping them realize what's possible. We connect vision to strategy, 
                    enabling coherent action and transforming complexity into pragmatic solutions.
                  </p>
                </div>
              )
            },
            {
              id: 'outcome-driver',
              title: 'Outcome Driver & Desired Outcome Framework',
              content: (
                <div className="grid lg:grid-cols-2 gap-8">
                  {/* Outcome Driver */}
                  <div className="bg-gradient-to-br from-amber-50 to-coral-50 p-8 border-2 border-[var(--mustard)]">
                    <h3 className="text-2xl font-heading font-bold text-charcoal-900 mb-6 text-center">Outcome Driver</h3>
                    
                    <div className="space-y-6">
                      <div className="bg-[var(--bg-raised)] p-6 border border-[var(--mustard)]">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 bg-[var(--mustard-dim)] rounded-full flex items-center justify-center">
                            <Target className="h-4 w-4 text-[var(--mustard)]" />
                          </div>
                          <h4 className="text-lg font-heading font-semibold text-charcoal-900">Current State</h4>
                        </div>
                        <p className="text-charcoal-700 font-body leading-relaxed">
                          Peak Priorities has a robust philosophy (DEGAP), proprietary models, and a deep content pipeline. 
                          We're building layered offerings to serve individuals and teams, with a strong exploration metaphor and design system.
                        </p>
                      </div>
                      
                      <div className="bg-[var(--bg-raised)] p-6 border border-coral-200">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 bg-coral-100 rounded-full flex items-center justify-center">
                            <ArrowRight className="h-4 w-4 text-coral-600" />
                          </div>
                          <h4 className="text-lg font-heading font-semibold text-charcoal-900">Problem/Opportunity</h4>
                        </div>
                        <p className="text-charcoal-700 font-body leading-relaxed">
                          The story exists in parts across docs, models, and in our heads; it isn't stitched into a single, 
                          visual "tour" inside the site. Harry needs to quickly see what Wes is building—framed as a narrative 
                          that links vision → systems → offerings.
                        </p>
                      </div>
                      
                      <div className="bg-[var(--bg-raised)] p-6 border border-consulting-slate-blue-200">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 bg-consulting-slate-blue-100 rounded-full flex items-center justify-center">
                            <Rocket className="h-4 w-4 text-consulting-slate-blue-600" />
                          </div>
                          <h4 className="text-lg font-heading font-semibold text-charcoal-900">Impact</h4>
                        </div>
                        <p className="text-charcoal-700 font-body leading-relaxed">
                          Without a concise, visual story, momentum stalls: alignment is assumed rather than demonstrated; 
                          feedback loops are slower; and we under-leverage our "Clarity company" promise. With it, Harry gains 
                          confidence we're aligned and the ability to steer with precision.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Desired Outcome Framework */}
                  <div className="bg-gradient-to-br from-sage-50 to-consulting-slate-blue-50 p-8 border-2 border-sage-200">
                    <h3 className="text-2xl font-heading font-bold text-charcoal-900 mb-6 text-center">Desired Outcome Framework</h3>
                    
                    <div className="space-y-6">
                      <div className="bg-[var(--bg-raised)] p-6 border border-sage-200">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 bg-sage-100 rounded-full flex items-center justify-center">
                            <Users className="h-4 w-4 text-sage-600" />
                          </div>
                          <h4 className="text-lg font-heading font-semibold text-charcoal-900">Who Wants It?</h4>
                        </div>
                        <p className="text-charcoal-700 font-body leading-relaxed">
                          Harry needs a shareable story that shows exactly what Wes is building and how it gets us from 
                          "where we are" to "where we're going."
                        </p>
                      </div>
                      
                      <div className="bg-[var(--bg-raised)] p-6 border border-sage-200">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 bg-consulting-slate-blue-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="h-4 w-4 text-consulting-slate-blue-600" />
                          </div>
                          <h4 className="text-lg font-heading font-semibold text-charcoal-900">What It Gives Him</h4>
                        </div>
                        <ul className="space-y-2 text-charcoal-700 font-body">
                          <li className="flex items-start gap-2">
                            <ArrowRight className="h-4 w-4 text-sage-600 mt-0.5 flex-shrink-0" />
                            <span>Confidence we're aligned</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ArrowRight className="h-4 w-4 text-sage-600 mt-0.5 flex-shrink-0" />
                            <span>A clear surface to give direction/feedback</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ArrowRight className="h-4 w-4 text-sage-600 mt-0.5 flex-shrink-0" />
                            <span>Visible forward momentum</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="bg-[var(--bg-raised)] p-6 border border-sage-200">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 bg-[var(--mustard-dim)] rounded-full flex items-center justify-center">
                            <BarChart3 className="h-4 w-4 text-[var(--mustard)]" />
                          </div>
                          <h4 className="text-lg font-heading font-semibold text-charcoal-900">Evidence It's Done</h4>
                        </div>
                        <p className="text-charcoal-700 font-body leading-relaxed">
                          Harry can clearly see the story of what we're doing, who it's for, why it's important, and how it works. 
                          The narrative is coherent, visible, and tells a complete story that he can understand and share with others.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            },
            {
              id: 'reason-to-change',
              title: 'The Reason to Change',
              content: (
                <div className="py-8">
                  <p className="text-lg text-charcoal-700 font-body text-center mb-8 max-w-3xl mx-auto">
                    Five primary drivers that show why change matters. Use one or stack several depending on audience.
                  </p>
                  
                  <div className="grid lg:grid-cols-2 gap-8">
                    {/* Driver A - Priority Thrash */}
                    <div className="p-6 border-l-4 border-consulting-slate-blue-600">
                      <h3 className="text-xl font-heading font-bold text-charcoal-900 mb-4">Driver A — Priority Thrash</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Current State (facts):</h4>
                          <p className="text-charcoal-700 font-body">Leaders and teams juggle too many "top" priorities. Backlogs swell. Decisions get revisited. Meetings multiply. Projects slip quietly, then loudly.</p>
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Problem/Opportunity (the gap):</h4>
                          <p className="text-charcoal-700 font-body">Work selection and sequencing aren't consistently tied to strategy; the system rewards activity over outcomes.</p>
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Impact (why it matters):</h4>
                          <p className="text-charcoal-700 font-body">Problem: burns resources, frays trust, and delays results. Opportunity: a shared prioritization model yields fewer, clearer bets and faster throughput.</p>
                        </div>
                      </div>
                    </div>

                    {/* Driver B - Misaligned Definitions */}
                    <div className="p-6 border-l-4 border-sage-600">
                      <h3 className="text-xl font-heading font-bold text-charcoal-900 mb-4">Driver B — Misaligned Definitions</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Current State:</h4>
                          <p className="text-charcoal-700 font-body">Key terms ("strategy," "priority," "capacity," "done," "ownership") are used inconsistently across functions.</p>
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Gap:</h4>
                          <p className="text-charcoal-700 font-body">People think they agree; they don't. Micro-misalignments compound into macro-misfires.</p>
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Impact:</h4>
                          <p className="text-charcoal-700 font-body">Problem: hidden rework, preventable conflict, diluted accountability. Opportunity: a shared lexicon enables cleaner handoffs, better decisions, and measurable progress.</p>
                        </div>
                      </div>
                    </div>

                    {/* Driver C - Fragmented Execution Loop */}
                    <div className="p-6 border-l-4 border-[var(--mustard)]">
                      <h3 className="text-xl font-heading font-bold text-charcoal-900 mb-4">Driver C — Fragmented Execution Loop</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Current State:</h4>
                          <p className="text-charcoal-700 font-body">Strategy, prioritization, planning, execution, and learning are owned by different tools and cadences. Feedback is slow, noisy, or lost.</p>
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Gap:</h4>
                          <p className="text-charcoal-700 font-body">No coherent cascade from why → what → when → how → what we learned.</p>
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Impact:</h4>
                          <p className="text-charcoal-700 font-body">Problem: drift and duplication. Opportunity: a single coherent loop tightens focus, reduces cycle time, and compounds learning.</p>
                        </div>
                      </div>
                    </div>

                    {/* Driver D - Personal Overload */}
                    <div className="p-6 border-l-4 border-coral-600">
                      <h3 className="text-xl font-heading font-bold text-charcoal-900 mb-4">Driver D — Personal Overload</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Current State:</h4>
                          <p className="text-charcoal-700 font-body">Calendar near 100% utilization; commitments exceed realistic capacity; family time compressed; sleep debt growing.</p>
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Gap:</h4>
                          <p className="text-charcoal-700 font-body">Priorities exist but are not sequenced; commitments are not renegotiated; attention is fragmented.</p>
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Impact:</h4>
                          <p className="text-charcoal-700 font-body">Problem: reduced judgment quality and diminished presence at home. Opportunity: a congruent system to select, sequence, and sustain the vital few—raising impact and well‑being.</p>
                        </div>
                      </div>
                    </div>

                    {/* Driver E - Team Chaos */}
                    <div className="p-6 border-l-4 border-consulting-slate-blue-600">
                      <h3 className="text-xl font-heading font-bold text-charcoal-900 mb-4">Driver E — Team Chaos</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Current State:</h4>
                          <p className="text-charcoal-700 font-body">27 active initiatives labeled "critical"; sprint goals unstable; rework and handoffs frequent; no common definitions of "bet," "priority," or "done."</p>
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Gap:</h4>
                          <p className="text-charcoal-700 font-body">Strategy doesn't consistently cascade into selection and sequencing; artifacts tell different stories.</p>
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Impact:</h4>
                          <p className="text-charcoal-700 font-body">Problem: wasted cycles and eroded trust. Opportunity: a shared model and lexicon that produce fewer, clearer bets—and a loop that ships reliably and learns quickly.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            },
            {
              id: 'who-we-serve',
              title: 'Who We Serve',
              content: (
                <div className="py-8">
                  <div className="grid lg:grid-cols-2 gap-8">
                    {/* Individuals */}
                    <div className="p-6 border-l-4 border-coral-600">
                      <h3 className="text-xl font-heading font-bold text-charcoal-900 mb-4">(1) Individuals (leaders, high performers)</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Their driver:</h4>
                          <p className="text-charcoal-700 font-body">D (Personal Overload), plus B (Definitions) when they need clean language to influence others.</p>
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Our promise:</h4>
                          <p className="text-charcoal-700 font-body">A portable, whole-life prioritization system that raises judgment quality, cleans up commitments, and creates congruence between what they want and what they do—at work and at home.</p>
                        </div>
                      </div>
                    </div>

                    {/* Teams/Organizations */}
                    <div className="p-6 border-l-4 border-consulting-slate-blue-600">
                      <h3 className="text-xl font-heading font-bold text-charcoal-900 mb-4">(2) Teams/Organizations</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Their driver:</h4>
                          <p className="text-charcoal-700 font-body">A (Priority Thrash) + C (Fragmented Loop), underpinned by B (Definitions) and E (Team Chaos).</p>
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-charcoal-900 mb-2">Our promise:</h4>
                          <p className="text-charcoal-700 font-body">A coherent strategy→priorities→plan→execution→learning loop supported by shared models, a common lexicon, and interoperable artifacts—so the org makes fewer, better bets and actually ships them.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            },
            {
              id: 'what-we-offer',
              title: 'What We Offer',
              content: (
                <div className="py-8">
                  <p className="text-lg text-charcoal-700 font-body text-center mb-8 max-w-3xl mx-auto">
                    Three layers of involvement designed for different needs and contexts
                  </p>
                  
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div className="space-y-8">
                      {/* Live & High-Touch */}
                      <div className="p-6 border-l-4 border-consulting-slate-blue-600">
                        <h3 className="text-xl font-heading font-bold text-charcoal-900 mb-4">Live & High-Touch</h3>
                        <ul className="space-y-2 text-charcoal-700 font-body">
                          <li>• Executive coaching & advisory (personal systems + leadership leverage)</li>
                          <li>• Team alignment interventions/workshops (strategy cascade, prioritization summits)</li>
                          <li>• Speaking & bespoke facilitation</li>
                        </ul>
                      </div>

                      {/* Structured Programs */}
                      <div className="p-6 border-l-4 border-sage-600">
                        <h3 className="text-xl font-heading font-bold text-charcoal-900 mb-4">Structured Programs</h3>
                        <ul className="space-y-2 text-charcoal-700 font-body">
                          <li>• Cohorts (e.g., Managing Priorities program)</li>
                          <li>• Short courses & sprints (prioritization, decision quality, state classification)</li>
                        </ul>
                      </div>
                    </div>

                    <div className="space-y-8">
                      {/* Products & Content */}
                      <div className="p-6 border-l-4 border-[var(--mustard)]">
                        <h3 className="text-xl font-heading font-bold text-charcoal-900 mb-4">Products & Content</h3>
                        <ul className="space-y-2 text-charcoal-700 font-body">
                          <li>• Book(s): Managing Priorities (Harry Max)</li>
                          <li>• Playbooks, templates, assessments, videos, clips</li>
                          <li>• Self-serve curriculum for individuals & teams (modular, stackable)</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Why Layers Matter */}
                  <div className="p-6 bg-charcoal-50 border border-charcoal-200">
                    <h3 className="text-lg font-heading font-bold text-charcoal-900 mb-3">Why Layers Matter</h3>
                    <p className="text-charcoal-700 font-body">
                      We serve many without diluting quality. Live work for leverage; programs for scale; content for reach. 
                      All powered by the same models, metadata, and design grammar, so the experience remains coherent.
                    </p>
                  </div>
                </div>
              )
            },
            {
              id: 'how-it-works',
              title: 'How It Works',
              content: (
                <div className="py-8">
                  <div className="space-y-12">
                    {/* Visual-Verbal Design System */}
                    <div>
                      <h3 className="text-2xl font-heading font-bold text-charcoal-900 mb-6 text-center">The Visual-Verbal Design System</h3>
                      <p className="text-lg text-charcoal-700 font-body text-center mb-8 max-w-3xl mx-auto">
                        Why clients "get it" quickly
                      </p>
                      
                      <div className="grid lg:grid-cols-3 gap-6">
                        <div className="p-6 border-l-4 border-consulting-slate-blue-600">
                          <h4 className="text-xl font-heading font-bold text-charcoal-900 mb-4">Visual Grammar</h4>
                          <p className="text-charcoal-700 font-body">
                            Colors, shapes, strokes, opacity, grid → each with semantic meaning (e.g., decision vs. hypothesis, commitment vs. capacity).
                          </p>
                        </div>
                        <div className="p-6 border-l-4 border-sage-600">
                          <h4 className="text-xl font-heading font-bold text-charcoal-900 mb-4">Language Discipline</h4>
                          <p className="text-charcoal-700 font-body">
                            Terms defined and disambiguated; every artifact references the lexicon.
                          </p>
                        </div>
                        <div className="p-6 border-l-4 border-[var(--mustard)]">
                          <h4 className="text-xl font-heading font-bold text-charcoal-900 mb-4">Component Library</h4>
                          <p className="text-charcoal-700 font-body">
                            Repeatable layouts for briefs, cascades, roadmaps, and scorecards.
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-8 p-6 bg-charcoal-50 border border-charcoal-200 text-center">
                        <h4 className="text-lg font-heading font-bold text-charcoal-900 mb-2">Result</h4>
                        <p className="text-charcoal-700 font-body">
                          Artifacts compose into a single story; people know how to read them at a glance.
                        </p>
                      </div>
                    </div>


                    {/* From Models to Reality */}
                    <div>
                      <h3 className="text-2xl font-heading font-bold text-charcoal-900 mb-6 text-center">From Models to Reality</h3>
                      <p className="text-lg text-charcoal-700 font-body text-center mb-8 max-w-3xl mx-auto">
                        M2 Model as M1 Template in M0 Runtime
                      </p>
                      
                      <div className="bg-[var(--bg-raised)] shadow-sm border border-charcoal-200 p-8">
                        <M2AsM1Template />
                      </div>
                    </div>
                  </div>
                </div>
              )
            }
          ]}
          defaultOpen={[]}
        />
      </div>





      {/* What is Peak Priorities Base Camp */}
      <div id="base-camp-section" className="bg-[var(--bg-raised)] shadow-sm border border-charcoal-200 p-8">
        <h2 className="text-2xl font-heading font-bold text-charcoal-900 mb-6">What is Peak Priorities Elevation?</h2>
        <div className="space-y-6">
          <p className="text-lg text-charcoal-700 leading-relaxed font-body">
            Peak Priorities Elevation is the internal application that powers the magic behind the curtain. 
            It's where we encode our models with meaningful metadata, power them through 12-16 internal engines, 
            and assemble resources into sequenced or bespoke paths—so ideas become value quickly and coherently.
          </p>
          
          <p className="text-charcoal-700 leading-relaxed font-body">
            This is the system that makes our client work possible. Built on a sophisticated four-layer knowledge 
            architecture, Elevation transforms abstract frameworks into practical tools, ready-to-use templates 
            into real-world results, and theoretical models into measurable outcomes. It's the engine room where 
            we turn complexity into coherent action for our clients.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-charcoal-900">Key Features</h3>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-charcoal-700">Four-layer Knowledge Architecture (M3 → M0)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-charcoal-700">Four-layer meta-stack representational grammar</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-charcoal-700">Comprehensive categorization across 8 major registry categories</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-charcoal-700">Structured relationships and system mappings</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-charcoal-700">Comprehensive evaluation and adaptation frameworks</span>
                </li>
              </ul>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-charcoal-900">Applications</h3>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-charcoal-700">Knowledge management and organization</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-charcoal-700">Framework and tool development</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-charcoal-700">System architecture and design</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-charcoal-700">Prioritization methodology optimization</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-charcoal-700">Organizational capability assessment</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Engines as Infrastructure */}
      <div className="bg-gradient-to-r from-charcoal-50 to-gray-50 p-8 border-2 border-charcoal-200">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="h-16 w-16 bg-charcoal-600 flex items-center justify-center shadow-md">
              <Settings className="h-10 w-10 text-white" />
            </div>
              </div>
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-3">
              <span className="px-3 py-1 bg-charcoal-600 text-white text-xs font-semibold rounded-full uppercase tracking-wide">
                Infrastructure
              </span>
              <span className="text-charcoal-700 font-mono text-sm">Platform Engines</span>
            </div>
            <h3 className="text-2xl font-bold text-charcoal-900 mb-3">
              Engines: The Power Behind the System
            </h3>
            <p className="text-charcoal-700 mb-4 leading-relaxed">
              While the Knowledge Architecture defines the structure of knowledge, engines provide the infrastructure 
              that powers the entire system. These orchestration engines govern the full content and value 
              lifecycle, ensuring clarity, coherence, and scalability across all layers.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-charcoal-900">Core Engines</h4>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-charcoal-600" />
                    <span className="text-sm text-charcoal-700">Knowledge Engine</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-charcoal-600" />
                    <span className="text-sm text-charcoal-700">Publishing Engine</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-charcoal-600" />
                    <span className="text-sm text-charcoal-700">Education Engine</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-charcoal-600" />
                    <span className="text-sm text-charcoal-700">Analytics Engine</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold text-charcoal-900">Specialized Engines</h4>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-charcoal-600" />
                    <span className="text-sm text-charcoal-700">Media Engine</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-charcoal-600" />
                    <span className="text-sm text-charcoal-700">Assembly Engine</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-charcoal-600" />
                    <span className="text-sm text-charcoal-700">Virtual Studio</span>
              </div>
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-charcoal-600" />
                    <span className="text-sm text-charcoal-700">Influence Engine</span>
              </div>
              </div>
              </div>
            </div>
            <div className="mt-4">
              <Link 
                to="/elevation/engines" 
                className="inline-flex items-center gap-2 px-4 py-2 bg-charcoal-600 text-white hover:bg-charcoal-700 transition-colors"
              >
                <Settings className="h-5 w-5" />
                Explore All Engines
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Knowledge Architecture Overview */}
      <div id="knowledge-architecture-section" className="bg-gradient-to-br from-consulting-slate-blue-50 via-sage-50 to-consulting-slate-blue-100 p-6 border-2 border-consulting-slate-blue-200">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-4 text-center">The Four-Layer Knowledge Architecture</h2>
        
        <div className="mb-6">
          <p className="text-base text-charcoal-700 max-w-4xl mx-auto leading-relaxed mb-4 text-center">
            A unified architecture for organizing all our knowledge assets—from the universal metadata that describes our frameworks (M3), 
            to the frameworks themselves (M2), to ready-to-deploy templates (M1), to actual experiences with measurable outcomes (M0). 
            Platform engines power the entire system as separate infrastructure, while feedback from real-world results continuously improves our models.
          </p>
          
          <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-300 p-4 max-w-4xl mx-auto mt-4">
            <p className="text-sm text-consulting-slate-blue-900 mb-2">
              <strong>What is the Knowledge Architecture?</strong> The Peak Priorities Knowledge Architecture is a systematic approach to organizing knowledge 
              for organizing knowledge systems. We've adapted it to create a four-layer stack where each level builds on the one below it:
            </p>
            <div className="grid grid-cols-4 gap-2 text-xs text-consulting-slate-blue-800 mt-3">
              <div className="text-center">
                <strong>M3:</strong> Metadata for describing models
              </div>
              <div className="text-center">
                <strong>M2:</strong> Models & frameworks
              </div>
              <div className="text-center">
                <strong>M1:</strong> Ready-to-use templates
              </div>
              <div className="text-center">
                <strong>M0:</strong> Real experiences
              </div>
            </div>
          </div>
        </div>
        
        {/* Horizontal layer cards */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {mofLayers.map((layer) => {
            const Icon = getIcon(layer.icon);
            const colors = getColorClasses(layer.color);
            const isSelected = selectedLayer === layer.id;
            const layerData = getLayerData(layer.id);
            
            return (
              <button
                key={layer.id}
                onClick={() => setSelectedLayer(isSelected ? null : layer.id)}
                className={`
                  p-4  border-2 transition-all text-left
                  ${isSelected 
                    ? `${colors.bg} ${colors.border} shadow-lg ring-2 ring-offset-2 ${colors.border}` 
                    : `bg-[var(--bg-raised)] ${colors.border} hover:${colors.bg} hover:shadow-lg`
                  }
                `}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className={`px-3 py-1 rounded font-bold text-lg ${colors.badge}`}>
                    {layer.level}
                  </div>
                  <Icon className={`h-6 w-6 ${colors.accent}`} />
                </div>
                <h3 className={`text-sm font-bold ${colors.text} mb-1`}>
                  {layer.level}
                </h3>
                <p className={`text-xs ${colors.text} mb-2 leading-relaxed`}>
                  {layer.description}
                </p>
                {layerData && (
                  <p className="text-xs text-charcoal-600">
                    {layerData.count} items
                  </p>
                )}
              </button>
            );
          })}
        </div>

      </div>

      {/* Layer Details */}
      {selectedLayer && (
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-charcoal-200 overflow-hidden">
          {mofLayers
            .filter(layer => layer.id === selectedLayer)
            .map(layer => {
              const Icon = getIcon(layer.icon);
              const colors = getColorClasses(layer.color);
              const layerData = getLayerData(layer.id);
              
              return (
                <div key={layer.id}>
                  <div className={`p-6 ${colors.bg} border-b-2 ${colors.border}`}>
            <div className="flex items-center space-x-3 mb-4">
                      <div className={`px-4 py-2  font-bold text-2xl ${colors.badge}`}>
                        {layer.level}
                      </div>
                      <Icon className={`h-10 w-10 ${colors.accent}`} />
                      <div>
                        <h2 className={`text-3xl font-bold ${colors.text}`}>
                          {layer.name}
                        </h2>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <h4 className={`text-sm font-semibold ${colors.accent} uppercase`}>Description</h4>
                        <p className={`${colors.text} text-lg`}>{layer.description}</p>
                      </div>
                      <div>
                        <h4 className={`text-sm font-semibold ${colors.accent} uppercase`}>Role</h4>
                        <p className={`${colors.text}`}>{layer.role}</p>
                      </div>
                      <div>
                        <h4 className={`text-sm font-semibold ${colors.accent} uppercase`}>Relationship</h4>
                        <p className={`${colors.text}`}>{layer.relationshipToNext}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Special content for M3: Meta-Stack - THE VITAL SECTION */}
                    {layer.id === 'm3' && (
                      <div className="border-t pt-6">
                        <h3 className="text-2xl font-bold text-sage-900 mb-4">
                          The Four-Layer Meta-Stack (M3 Grammar)
                        </h3>
                        <p className="text-sage-700 mb-4">
                          The Four-Layer Meta-Stack IS the M3 layer - the universal grammar for representing all knowledge.
                          It provides the fields and structure for defining all models at M2.
                        </p>
                        <div className="mb-6">
                          <Link 
                            to="/elevation/grammar" 
                            className="inline-flex items-center gap-2 px-4 py-2 bg-sage-600 text-white hover:bg-sage-700 transition-colors"
                          >
                            <Layers className="h-5 w-5" />
                            Explore Full Grammar Details
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                        </div>
                        <div className="space-y-3">
                          {metaStackAsM3.layers.map((stackLayer, idx) => (
                            <div key={idx} className="bg-[var(--bg-raised)] border-2 border-sage-200 p-4">
                              <h4 className="font-bold text-sage-900 text-lg">
                                Layer {idx + 1}: {stackLayer.name}
                              </h4>
                              <p className="text-sage-700 text-sm mt-1">
                                {stackLayer.description}
                              </p>
                              <p className="text-charcoal-700 text-sm mt-2">
                                <span className="font-semibold">Provides:</span> {stackLayer.provides}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Examples from layer definition - show for all except M3 */}
                    {layer.examples.length > 0 && layer.id !== 'm3' && (
              <div>
                        <h3 className="text-xl font-bold text-charcoal-900 mb-3">
                          Examples at {layer.level}
                        </h3>
                        <div className="grid md:grid-cols-2 gap-3">
                          {layer.examples.map((example, idx) => (
                            <div key={idx} className={`px-4 py-2  ${colors.badge}`}>
                              {example}
                            </div>
                          ))}
              </div>
            </div>
                    )}

                    {/* Data counts and samples - skip for M3 since it's already shown in detail in Meta-Stack tab */}
                    {layerData && layer.id !== 'm3' && (
                      <div className="border-t pt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-2xl font-bold text-charcoal-900">
                            {layerData.title} ({layerData.count} total)
                          </h3>
                          {layer.id === 'm2' && (
                            <Link 
                              to="/elevation/models" 
                              className={`inline-flex items-center gap-2 px-4 py-2 ${colors.button}  transition-colors`}
                            >
                              View All Models
                              <ChevronRight className="h-4 w-4" />
                            </Link>
                          )}
                          {layer.id === 'm1' && (
                            <Link 
                              to="/elevation/templates" 
                              className={`inline-flex items-center gap-2 px-4 py-2 ${colors.button}  transition-colors`}
                            >
                              View All Templates
                              <ChevronRight className="h-4 w-4" />
                            </Link>
                          )}
                          {layer.id === 'm0' && (
                            <Link 
                              to="/elevation/runtime" 
                              className={`inline-flex items-center gap-2 px-4 py-2 ${colors.button}  transition-colors`}
                            >
                              View All Instances
                              <ChevronRight className="h-4 w-4" />
                            </Link>
                          )}
                        </div>
                        <div className="space-y-2">
                          {layerData.items.map((item, idx) => (
                            <div key={idx} className={`px-4 py-3  ${colors.bg} border ${colors.border}`}>
                              <p className="text-sm text-charcoal-800">{item}</p>
                            </div>
                          ))}
                          {layerData.count > layerData.items.length && (
                            <div className="text-center py-2">
                              <p className="text-sm text-charcoal-500">
                                ... and {layerData.count - layerData.items.length} more
                              </p>
                            </div>
                          )}
                        </div>
              </div>
                    )}
              </div>
              </div>
              );
            })}
              </div>
      )}


      {/* Key Insights */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-100 p-8 border-2 border-consulting-slate-blue-300">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Key Insights</h2>
        <div className="space-y-4">
          <div className="bg-[var(--bg-raised)] p-4 border-l-4 border-sage-500">
            <h3 className="font-bold text-sage-900 mb-2">M3: Meta-Stack = Meta-Model (Metadata)</h3>
            <p className="text-charcoal-700">
              The Four-Layer Meta-Stack (Ontology, Modality, Dynamics, Evaluation) IS the M3 meta-model. 
              It's the universal metadata that defines HOW to describe models. It provides the fields and 
              structure for defining all models at M2.
            </p>
        </div>
          <div className="bg-[var(--bg-raised)] p-4 border-l-4 border-consulting-slate-blue-500">
            <h3 className="font-bold text-consulting-slate-blue-900 mb-2">M2: Registry Entries = Model</h3>
            <p className="text-charcoal-700">
              Each registry entry (like "Five Stages of DEGAP" or "Six Kinds of Items") is a model - 
              defined using the M3 meta-model metadata. These are the actual frameworks and systems that 
              can be instantiated as concrete templates at M1.
            </p>
          </div>
          <div className="bg-[var(--bg-raised)] p-4 border-l-4 border-sage-500">
            <h3 className="font-bold text-sage-900 mb-2">M1: Templates (Instances of Models)</h3>
            <p className="text-charcoal-700">
              Templates are M1 - concrete instances of M2 models. For example, 
              "DEGAP Workshop Design (2-day)" is a template that instantiates the "Five Stages of DEGAP" model 
              with specific parameters ready for deployment.
            </p>
          </div>
          <div className="bg-[var(--bg-raised)] p-4 border-l-4 border-burnt-500">
            <h3 className="font-bold text-burnt-900 mb-2">M0: Instances (Real Experiences)</h3>
            <p className="text-charcoal-700">
              Actual workshops, assessments, and coaching sessions are M0 instances - 
              deployments of M1 templates with specific participants, dates, and measurable outcomes. 
              Example: "TechCorp Q4 2024 Workshop - 12 participants, 95% satisfaction".
            </p>
          </div>
        </div>
      </div>

      {/* The Lab - Apps Under Development */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-8 border-2 border-[var(--mustard)]">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="h-16 w-16 bg-[var(--mustard)] flex items-center justify-center shadow-md">
              <FlaskConical className="h-10 w-10 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-3">
              <span className="px-3 py-1 bg-[var(--mustard)] text-white text-xs font-semibold rounded-full uppercase tracking-wide">
                The Lab
              </span>
              <span className="text-[var(--mustard)] font-mono text-sm">Apps Under Development</span>
            </div>
            <h3 className="text-2xl font-bold text-[var(--mustard)] mb-3">
              Experimental Features & Development Tools
            </h3>
            <p className="text-[var(--mustard)] mb-4 leading-relaxed">
              The Lab contains experimental applications and development tools that are actively being refined. 
              These features represent the cutting edge of our knowledge system and provide early access to 
              new capabilities and methodologies.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-[var(--mustard)]">Featured Applications</h4>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Map className="h-4 w-4 text-[var(--mustard)]" />
                    <span className="text-sm text-[var(--mustard)]">Island Explorer</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mountain className="h-4 w-4 text-[var(--mustard)]" />
                    <span className="text-sm text-[var(--mustard)]">Mountain Ascent</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-[var(--mustard)]" />
                    <span className="text-sm text-[var(--mustard)]">Outcomes Tracking</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Rocket className="h-4 w-4 text-[var(--mustard)]" />
                    <span className="text-sm text-[var(--mustard)]">Workshops</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold text-[var(--mustard)]">Other Tools in Development</h4>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-[var(--mustard)]" />
                    <span className="text-sm text-[var(--mustard)]">Team Collaboration</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-[var(--mustard)]" />
                    <span className="text-sm text-[var(--mustard)]">Analytics Dashboard</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <StickyNote className="h-4 w-4 text-[var(--mustard)]" />
                    <span className="text-sm text-[var(--mustard)]">Framework Notes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[var(--mustard)]" />
                    <span className="text-sm text-[var(--mustard)]">Knowledge Navigator (AI)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-[var(--mustard)]" />
                    <span className="text-sm text-[var(--mustard)]">Progress & Game Mechanics</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-[var(--mustard)]" />
                    <span className="text-sm text-[var(--mustard)]">Feature Flags</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Separator */}
      <div className="border-t border-charcoal-200 my-12"></div>

      {/* Story Elements & Engines */}
      <div id="story-elements-section" className="py-8">
        <h2 className="text-2xl font-heading font-bold text-charcoal-900 mb-6 text-center">The Story & The Power Behind It</h2>
        
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Story Elements */}
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-xl font-heading font-semibold text-charcoal-900 mb-3">Our Story Elements</h3>
              <p className="text-charcoal-700 font-body">
                The narrative that connects vision to execution
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="bg-[var(--bg-raised)] p-4 border border-sage-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-consulting-slate-blue-100 rounded-full flex items-center justify-center">
                    <Target className="h-4 w-4 text-consulting-slate-blue-600" />
                  </div>
                  <h4 className="font-heading font-semibold text-charcoal-900">The Problem We Solve</h4>
                </div>
                <p className="text-sm text-charcoal-600 font-body">Most teams drown in competing priorities and false urgency</p>
              </div>
              
              <div className="bg-[var(--bg-raised)] p-4 border border-sage-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-sage-100 rounded-full flex items-center justify-center">
                    <Users className="h-4 w-4 text-sage-600" />
                  </div>
                  <h4 className="font-heading font-semibold text-charcoal-900">Who We Serve</h4>
                </div>
                <p className="text-sm text-charcoal-600 font-body">Individuals, teams, and organizations seeking clarity</p>
              </div>
              
              <div className="bg-[var(--bg-raised)] p-4 border border-sage-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-[var(--mustard-dim)] rounded-full flex items-center justify-center">
                    <Compass className="h-4 w-4 text-[var(--mustard)]" />
                  </div>
                  <h4 className="font-heading font-semibold text-charcoal-900">Our Approach</h4>
                </div>
                <p className="text-sm text-charcoal-600 font-body">Four-layer knowledge architecture (M3-M0) with visual-verbal design system and 12+ specialized engines</p>
              </div>
              
              <div className="bg-[var(--bg-raised)] p-4 border border-sage-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-coral-100 rounded-full flex items-center justify-center">
                    <BookOpen className="h-4 w-4 text-coral-600" />
                  </div>
                  <h4 className="font-heading font-semibold text-charcoal-900">Our Proof</h4>
                </div>
                <p className="text-sm text-charcoal-600 font-body">Managing Priorities methodology, DEGAP framework, and systematic approach to turning complexity into coherent action</p>
              </div>
            </div>
          </div>
          
          {/* Engines */}
          <div id="engines-section" className="space-y-6">
            <div className="text-center">
              <h3 className="text-xl font-heading font-semibold text-charcoal-900 mb-3">The Engines: Power Behind the System</h3>
              <p className="text-charcoal-700 font-body">
                The infrastructure that makes everything work
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[var(--bg-raised)] p-3 border border-consulting-slate-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <Settings className="h-3 w-3 text-consulting-slate-blue-600" />
                  <h5 className="font-heading font-semibold text-charcoal-900 text-xs">Knowledge Engine</h5>
                </div>
                <p className="text-xs text-charcoal-600 font-body">Core system for storing and organizing</p>
              </div>
              
              <div className="bg-[var(--bg-raised)] p-3 border border-consulting-slate-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <Settings className="h-3 w-3 text-consulting-slate-blue-600" />
                  <h5 className="font-heading font-semibold text-charcoal-900 text-xs">Publishing Engine</h5>
                </div>
                <p className="text-xs text-charcoal-600 font-body">Distributes content across channels</p>
              </div>
              
              <div className="bg-[var(--bg-raised)] p-3 border border-consulting-slate-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <Settings className="h-3 w-3 text-consulting-slate-blue-600" />
                  <h5 className="font-heading font-semibold text-charcoal-900 text-xs">Education Engine</h5>
                </div>
                <p className="text-xs text-charcoal-600 font-body">Delivers learning experiences</p>
              </div>
              
              <div className="bg-[var(--bg-raised)] p-3 border border-consulting-slate-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <Settings className="h-3 w-3 text-consulting-slate-blue-600" />
                  <h5 className="font-heading font-semibold text-charcoal-900 text-xs">Analytics Engine</h5>
                </div>
                <p className="text-xs text-charcoal-600 font-body">Collects data and generates insights</p>
              </div>
              
              <div className="bg-[var(--bg-raised)] p-3 border border-consulting-slate-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <Settings className="h-3 w-3 text-consulting-slate-blue-600" />
                  <h5 className="font-heading font-semibold text-charcoal-900 text-xs">Media Engine</h5>
                </div>
                <p className="text-xs text-charcoal-600 font-body">Manages all media assets</p>
              </div>
              
              <div className="bg-[var(--bg-raised)] p-3 border border-consulting-slate-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <Settings className="h-3 w-3 text-consulting-slate-blue-600" />
                  <h5 className="font-heading font-semibold text-charcoal-900 text-xs">Assembly Engine</h5>
                </div>
                <p className="text-xs text-charcoal-600 font-body">Composes final deliverables</p>
              </div>
              
              <div className="bg-[var(--bg-raised)] p-3 border border-consulting-slate-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <Settings className="h-3 w-3 text-consulting-slate-blue-600" />
                  <h5 className="font-heading font-semibold text-charcoal-900 text-xs">Studio Engine</h5>
                </div>
                <p className="text-xs text-charcoal-600 font-body">Professional asset creation tools</p>
              </div>
              
              <div className="bg-[var(--bg-raised)] p-3 border border-consulting-slate-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <Settings className="h-3 w-3 text-consulting-slate-blue-600" />
                  <h5 className="font-heading font-semibold text-charcoal-900 text-xs">Orchestration</h5>
                </div>
                <p className="text-xs text-charcoal-600 font-body">Coordinates workflows</p>
              </div>
            </div>
            
            <div className="text-center pt-4">
              <Link 
                to="/elevation/engines" 
                className="inline-flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors text-sm font-body"
              >
                <Settings className="h-4 w-4" />
                Explore All Engines
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};